import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  RefreshCw, Plus, Clock, AlertTriangle, X, CheckCircle, Download, Play,
  Activity, ChevronDown, ChevronRight, FileText, Send, Loader2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const authHeaders = () => {
  const t = localStorage.getItem('crm_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// =============================================================================
// Status-System
// =============================================================================

const STATUS_OPTIONS = [
  { value: 'pending',             label: 'Pending',         cls: 'bg-gray-100 text-gray-600' },
  { value: 'intake_in_progress',  label: 'Intake läuft',    cls: 'bg-blue-100 text-blue-700' },
  { value: 'intake_done',         label: 'Intake fertig',   cls: 'bg-cyan-100 text-cyan-700' },
  { value: 'pipeline_running',    label: 'Pipeline läuft',  cls: 'bg-amber-100 text-amber-700' },
  { value: 'draft_ready',         label: 'Draft bereit',    cls: 'bg-purple-100 text-purple-700' },
  { value: 'reviewed',            label: 'Reviewed',        cls: 'bg-emerald-100 text-emerald-700' },
  { value: 'delivered',           label: 'Ausgeliefert',    cls: 'bg-[#658a07]/15 text-[#658a07]' },
  { value: 'failed',              label: 'Fehlgeschlagen',  cls: 'bg-red-100 text-red-700' },
];

function StatusPill({ value }) {
  const opt = STATUS_OPTIONS.find((o) => o.value === value) || { label: value || '—', cls: 'bg-gray-100 text-gray-600' };
  return <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${opt.cls}`}>{opt.label}</span>;
}

function formatGermanDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
}

// =============================================================================
// Mini-Markdown-Renderer (Headings, Bold, Italic, Lists, Paragraphs)
// =============================================================================

function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split(/\r?\n/);
  const blocks = [];
  let listBuffer = null;
  let paraBuffer = [];

  const flushPara = () => {
    if (paraBuffer.length > 0) {
      blocks.push({ type: 'p', text: paraBuffer.join(' ') });
      paraBuffer = [];
    }
  };
  const flushList = () => {
    if (listBuffer) {
      blocks.push({ type: 'ul', items: listBuffer });
      listBuffer = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushPara();
      flushList();
      continue;
    }
    if (line.startsWith('# ')) {
      flushPara(); flushList();
      blocks.push({ type: 'h1', text: line.slice(2) });
    } else if (line.startsWith('## ')) {
      flushPara(); flushList();
      blocks.push({ type: 'h2', text: line.slice(3) });
    } else if (line.startsWith('### ')) {
      flushPara(); flushList();
      blocks.push({ type: 'h3', text: line.slice(4) });
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      flushPara();
      if (!listBuffer) listBuffer = [];
      listBuffer.push(line.slice(2));
    } else {
      flushList();
      paraBuffer.push(line);
    }
  }
  flushPara(); flushList();

  const inline = (text) => {
    const parts = [];
    let rest = text;
    let key = 0;
    const re = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
    let last = 0; let m;
    while ((m = re.exec(rest)) !== null) {
      if (m.index > last) parts.push(<span key={key++}>{rest.slice(last, m.index)}</span>);
      const tok = m[0];
      if (tok.startsWith('**')) parts.push(<strong key={key++}>{tok.slice(2, -2)}</strong>);
      else if (tok.startsWith('*')) parts.push(<em key={key++}>{tok.slice(1, -1)}</em>);
      else if (tok.startsWith('`')) parts.push(<code key={key++} className="bg-gray-100 px-1 py-0.5 rounded text-xs">{tok.slice(1, -1)}</code>);
      last = m.index + tok.length;
    }
    if (last < rest.length) parts.push(<span key={key++}>{rest.slice(last)}</span>);
    return parts;
  };

  return (
    <div className="prose-sm leading-relaxed text-[#1f1f1f]">
      {blocks.map((b, i) => {
        if (b.type === 'h1') return <h1 key={i} className="text-2xl font-bold mt-6 mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>{inline(b.text)}</h1>;
        if (b.type === 'h2') return <h2 key={i} className="text-xl font-bold mt-5 mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>{inline(b.text)}</h2>;
        if (b.type === 'h3') return <h3 key={i} className="text-base font-bold mt-4 mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>{inline(b.text)}</h3>;
        if (b.type === 'ul') return (
          <ul key={i} className="list-disc pl-6 my-2 space-y-1 text-sm">
            {b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}
          </ul>
        );
        return <p key={i} className="my-2 text-sm">{inline(b.text)}</p>;
      })}
    </div>
  );
}

// =============================================================================
// Hauptkomponente
// =============================================================================

export default function ReadyChecksAdmin() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [activeRunId, setActiveRunId] = useState(null);
  const [showStart, setShowStart] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/admin/readycheck`, { headers: authHeaders() });
      setRuns(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Laden fehlgeschlagen');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = runs.filter((r) => filter === 'all' ? true : r.status === filter);

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1f1f1f] mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Ready Checks
          </h1>
          <p className="text-sm text-gray-500">Modul 1 — Multi-Agent KI-Diagnose. Drafts werden manuell reviewt vor Auslieferung.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="inline-flex items-center gap-2 border border-gray-300 px-3 py-2 text-xs uppercase tracking-wider hover:bg-gray-50">
            <RefreshCw size={14} /> Neu laden
          </button>
          <button onClick={() => setShowStart(true)}
            className="inline-flex items-center gap-2 bg-[#0a0a0a] text-[#befa0a] px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#befa0a] hover:text-[#0a0a0a]"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            <Plus size={14} /> Neuer Ready Check
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <Stat label="Gesamt" value={runs.length} />
        <Stat label="Draft bereit" value={runs.filter((r) => r.status === 'draft_ready').length} color="text-[#e07a2f]" />
        <Stat label="Reviewed" value={runs.filter((r) => r.status === 'reviewed').length} color="text-emerald-600" />
        <Stat label="Ausgeliefert" value={runs.filter((r) => r.status === 'delivered').length} color="text-[#658a07]" />
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 mb-4 text-sm"><AlertTriangle size={14} className="inline mr-2" /> {error}</div>}

      {/* Filter */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setFilter('all')}
          className={`text-xs uppercase tracking-wider px-3 py-1.5 border transition-colors ${filter === 'all' ? 'bg-[#0a0a0a] text-[#befa0a] border-[#0a0a0a]' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
          style={{ fontFamily: "'Oswald', sans-serif" }}>
          Alle ({runs.length})
        </button>
        {STATUS_OPTIONS.map((o) => {
          const count = runs.filter((x) => x.status === o.value).length;
          if (count === 0) return null;
          return (
            <button key={o.value} onClick={() => setFilter(o.value)}
              className={`text-xs uppercase tracking-wider px-3 py-1.5 border transition-colors ${filter === o.value ? 'bg-[#0a0a0a] text-[#befa0a] border-[#0a0a0a]' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              {o.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Liste */}
      {loading ? (
        <div className="p-12 text-center text-gray-400">Lade…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-gray-300 p-12 text-center text-gray-400">
          Keine Ready Checks{filter !== 'all' ? ` mit Status "${filter}"` : ''}. Klick auf "Neuer Ready Check" zum Starten.
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-2.5">Eingang</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Firma</th>
                <th className="px-4 py-2.5 hidden md:table-cell">Branche</th>
                <th className="px-4 py-2.5 hidden md:table-cell text-right">Kosten</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setActiveRunId(r.run_id)}>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    <Clock size={11} className="inline mr-1 opacity-50" />
                    {formatGermanDate(r.created_at)}
                  </td>
                  <td className="px-4 py-3"><StatusPill value={r.status} /></td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#1f1f1f]">{r.company_name || '—'}</div>
                    <div className="text-xs text-gray-400 font-mono">{r.run_id}</div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs text-gray-600">{r.industry || '—'}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-right text-xs text-gray-600">
                    {r.estimated_cost_usd ? `${parseFloat(r.estimated_cost_usd).toFixed(2)} $` : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-[#658a07] text-xs uppercase tracking-wider font-bold">Öffnen →</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showStart && <StartReadyCheckModal onClose={() => setShowStart(false)} onCreated={(runId) => { setShowStart(false); setActiveRunId(runId); load(); }} />}
      {activeRunId && <ReadyCheckDetailModal runId={activeRunId} onClose={() => { setActiveRunId(null); load(); }} />}
    </div>
  );
}

// =============================================================================
// Start-Modal: Transkript → Extract → Interview → Finalize → Run
// =============================================================================

function StartReadyCheckModal({ onClose, onCreated }) {
  const [stage, setStage] = useState('input'); // 'input' | 'interview' | 'starting'
  const [transcript, setTranscript] = useState('');
  const [intakeChat, setIntakeChat] = useState('');
  const [companyHint, setCompanyHint] = useState('');
  const [industryHint, setIndustryHint] = useState('');
  const [leadId, setLeadId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [runId, setRunId] = useState('');
  const [partialIntake, setPartialIntake] = useState(null);
  const [interviewPlan, setInterviewPlan] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [answers, setAnswers] = useState({}); // { key: 'antwort' }

  const startExtract = async () => {
    if (!transcript.trim() && !intakeChat.trim()) {
      setError('Bitte mindestens Transkript ODER Intake-Chat füllen.');
      return;
    }
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/admin/readycheck/start`, {
        transcript, intake_chat: intakeChat,
        company_hint: companyHint || undefined,
        industry_hint: industryHint || undefined,
        lead_id: leadId || undefined,
      }, { headers: authHeaders() });
      setRunId(data.run_id);
      setPartialIntake(data.partial_intake);
      setInterviewPlan(data.interview_plan);
      setGaps(data.gaps || []);
      setStage('interview');
    } catch (err) {
      setError(err.response?.data?.detail || 'Start fehlgeschlagen');
    }
    setBusy(false);
  };

  const finalizeAndRun = async () => {
    setBusy(true); setError('');
    setStage('starting');
    try {
      const answerList = (interviewPlan?.questions || []).map((q) => ({
        key: q.key, answer: answers[q.key] || '',
      })).filter((a) => a.answer.trim());

      // Finalize Intake
      await axios.post(`${API}/admin/readycheck/${runId}/finalize`, { answers: answerList }, { headers: authHeaders() });

      // Run Pipeline (Background)
      await axios.post(`${API}/admin/readycheck/${runId}/run`, { allow_branch_generation: true }, { headers: authHeaders() });

      onCreated(runId);
    } catch (err) {
      setError(err.response?.data?.detail || 'Finalize/Run fehlgeschlagen');
      setStage('interview');
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white w-full max-w-[800px] max-h-[calc(100vh-2rem)] overflow-y-auto">
        <div className="bg-[#0a0a0a] text-white px-6 py-4 flex justify-between items-start">
          <div>
            <p className="text-[#befa0a] text-[10px] uppercase tracking-[0.3em] font-bold mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
              Neuer Ready Check · {stage === 'input' ? 'Eingabe' : stage === 'interview' ? 'Interview' : 'Pipeline startet'}
            </p>
            <h3 className="text-lg font-bold uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
              Modul 1 · KI Ready Check
            </h3>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X size={20} /></button>
        </div>

        {error && <div className="bg-red-50 border-b border-red-200 text-red-700 p-3 text-sm"><AlertTriangle size={14} className="inline mr-2" /> {error}</div>}

        {stage === 'input' && (
          <div className="p-6 space-y-5">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Kickoff-Call-Transkript</label>
              <textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} rows={10}
                placeholder="Rohtext aus dem Kickoff-Gespräch (wird automatisch PII-bereinigt)"
                className="w-full border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#befa0a] resize-y" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Intake-Chat (optional)</label>
              <textarea value={intakeChat} onChange={(e) => setIntakeChat(e.target.value)} rows={4}
                placeholder="Zusätzlicher Chat-Verlauf aus Vor-Kontakt — kann leer bleiben."
                className="w-full border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#befa0a] resize-y" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Firma (Hint)</label>
                <input type="text" value={companyHint} onChange={(e) => setCompanyHint(e.target.value)} placeholder="z.B. Müller SHK GmbH"
                  className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Branche (Hint)</label>
                <input type="text" value={industryHint} onChange={(e) => setIndustryHint(e.target.value)} placeholder="z.B. SHK Handwerk"
                  className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
              </div>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Lead-ID (optional)</label>
              <input type="text" value={leadId} onChange={(e) => setLeadId(e.target.value)} placeholder="UUID aus dem Leads-Board"
                className="w-full border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#befa0a]" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider text-gray-500 hover:text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>Abbrechen</button>
              <button onClick={startExtract} disabled={busy}
                className="bg-[#0a0a0a] text-[#befa0a] px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#befa0a] hover:text-[#0a0a0a] disabled:opacity-50 inline-flex items-center gap-2"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                {busy ? <><Loader2 size={14} className="animate-spin" /> Extrahiere…</> : <><Send size={14} /> Extraktion starten</>}
              </button>
            </div>
          </div>
        )}

        {stage === 'interview' && interviewPlan && (
          <div className="p-6 space-y-5">
            <div className="bg-blue-50 border border-blue-200 p-3 text-xs text-blue-700">
              <strong>Run-ID:</strong> <code className="font-mono">{runId}</code><br />
              <strong>Erkannt:</strong> {partialIntake?.company_name || '(Unternehmen unbekannt)'} · {partialIntake?.industry || '(Branche unbekannt)'} · {partialIntake?.industry_key && <span className="text-blue-600">Branch-Key: {partialIntake.industry_key}</span>}<br />
              <strong>Gaps:</strong> {gaps.length > 0 ? gaps.join(', ') : '(keine)'}
            </div>

            {interviewPlan.rationale && (
              <p className="text-xs text-gray-500 italic">{interviewPlan.rationale}</p>
            )}

            <div className="space-y-4">
              {interviewPlan.questions.map((q, idx) => (
                <div key={q.key} className="border border-gray-200 p-4 bg-gray-50/30">
                  <div className="flex items-start justify-between mb-1">
                    <p className="text-sm font-bold text-[#1f1f1f]">{idx + 1}. {q.question}</p>
                    {q.is_kpi_alternative && <span className="text-[10px] bg-[#befa0a]/30 text-[#658a07] px-2 py-0.5 rounded uppercase tracking-wider font-bold">KPI</span>}
                  </div>
                  {q.why_we_ask && <p className="text-xs text-gray-500 mb-2 italic">{q.why_we_ask}</p>}
                  {q.expected_kind === 'enum' && q.enum_options?.length > 0 ? (
                    <select value={answers[q.key] || ''} onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
                      className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]">
                      <option value="">— bitte wählen —</option>
                      {q.enum_options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  ) : (
                    <textarea value={answers[q.key] || ''} onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
                      rows={q.expected_kind === 'text' ? 2 : 1}
                      placeholder={q.expected_kind === 'number' ? 'Zahl eingeben' : 'Antwort eingeben…'}
                      className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a] resize-y" />
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-between items-center pt-2">
              <p className="text-xs text-gray-500">
                {Object.values(answers).filter((a) => (a || '').trim()).length} / {interviewPlan.questions.length} beantwortet
              </p>
              <div className="flex gap-3">
                <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider text-gray-500 hover:text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>Später</button>
                <button onClick={finalizeAndRun} disabled={busy}
                  className="bg-[#0a0a0a] text-[#befa0a] px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#befa0a] hover:text-[#0a0a0a] disabled:opacity-50 inline-flex items-center gap-2"
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {busy ? <><Loader2 size={14} className="animate-spin" /> Speichere…</> : <><Play size={14} /> Pipeline starten</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {stage === 'starting' && (
          <div className="p-12 text-center space-y-3">
            <Loader2 size={32} className="animate-spin mx-auto text-[#658a07]" />
            <p className="text-sm text-gray-600">Pipeline startet im Hintergrund…</p>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Detail-Modal mit Polling
// =============================================================================

function ReadyCheckDetailModal({ runId, onClose }) {
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [openSections, setOpenSections] = useState({ intake: false, branch: false, grounding: false, assessments: false, synthesis: true, report: true, flags: true });
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/readycheck/${runId}`, { headers: authHeaders() });
      setRun(data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Laden fehlgeschlagen');
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => { load(); }, [load]);

  // Polling während Pipeline läuft
  useEffect(() => {
    if (!run) return;
    const isRunning = ['pipeline_running', 'intake_in_progress'].includes(run.status);
    if (isRunning) {
      pollRef.current = setInterval(load, 5000);
      return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }
    if (pollRef.current) clearInterval(pollRef.current);
  }, [run, load]);

  const patchStatus = async (newStatus) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/readycheck/${runId}`, { status: newStatus, reviewed_by: 'sebastian' }, { headers: authHeaders() });
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Status-Update fehlgeschlagen');
    }
    setBusy(false);
  };

  const downloadMarkdown = () => {
    const url = `${API}/admin/readycheck/${runId}/report.md`;
    const t = localStorage.getItem('crm_token');
    fetch(url, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `ready-check-${runId}.md`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => alert('Download fehlgeschlagen'));
  };

  if (loading) return (
    <Modal onClose={onClose}>
      <div className="p-12 text-center"><Loader2 size={24} className="animate-spin mx-auto text-gray-400" /></div>
    </Modal>
  );

  if (error || !run) return (
    <Modal onClose={onClose}>
      <div className="p-6 text-red-600">{error || 'Nicht gefunden'}</div>
    </Modal>
  );

  const assessments = run.assessments || [];
  const synthesis = run.synthesis || {};
  const flags = run.quality_flags || [];
  const isRunning = ['pipeline_running', 'intake_in_progress'].includes(run.status);

  return (
    <Modal onClose={onClose} wide>
      {/* Header */}
      <div className="bg-[#0a0a0a] text-white px-6 py-4 sticky top-0 z-10">
        <div className="flex justify-between items-start gap-4">
          <div className="min-w-0">
            <p className="text-[#befa0a] text-[10px] uppercase tracking-[0.3em] font-bold mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
              Ready Check
            </p>
            <h3 className="text-lg font-bold uppercase truncate" style={{ fontFamily: "'Oswald', sans-serif" }}>
              {run.company_name || '—'} · {run.industry || '—'}
            </h3>
            <p className="text-[11px] font-mono opacity-60 mt-1">{run.run_id}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <StatusPill value={run.status} />
            <button onClick={onClose} className="text-white/60 hover:text-white"><X size={20} /></button>
          </div>
        </div>

        {isRunning && (
          <div className="mt-3 flex items-center gap-2 text-xs text-amber-200 bg-amber-900/30 px-3 py-1.5 rounded">
            <Loader2 size={12} className="animate-spin" /> Pipeline läuft — Polling alle 5s
          </div>
        )}

        {run.error && (
          <div className="mt-3 text-xs text-red-200 bg-red-900/30 px-3 py-1.5 rounded">
            <AlertTriangle size={12} className="inline mr-1" /> {run.error}
          </div>
        )}
      </div>

      {/* Aktionen */}
      <div className="bg-gray-50 border-b border-gray-200 px-6 py-3 flex flex-wrap gap-2">
        {run.status === 'draft_ready' && (
          <button onClick={() => patchStatus('reviewed')} disabled={busy}
            className="bg-emerald-600 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            <CheckCircle size={14} /> Als reviewed markieren
          </button>
        )}
        {run.status === 'reviewed' && (
          <button onClick={() => patchStatus('delivered')} disabled={busy}
            className="bg-[#658a07] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#496306] disabled:opacity-50 inline-flex items-center gap-2"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            <Send size={14} /> Als ausgeliefert markieren
          </button>
        )}
        {(run.status === 'draft_ready' || run.status === 'reviewed' || run.status === 'delivered') && run.report_markdown && (
          <button onClick={downloadMarkdown}
            className="border border-gray-300 text-[#1f1f1f] px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-gray-100 inline-flex items-center gap-2"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            <Download size={14} /> Markdown
          </button>
        )}
        {run.estimated_cost_usd > 0 && (
          <span className="ml-auto text-xs text-gray-500 inline-flex items-center gap-1">
            <Activity size={12} /> Kosten: {parseFloat(run.estimated_cost_usd).toFixed(2)} $
          </span>
        )}
      </div>

      {/* Body */}
      <div className="p-6 space-y-3 bg-white">
        {/* Quality Flags */}
        {flags.length > 0 && (
          <Collapsible
            title={<>Quality-Flags <span className="text-xs text-amber-600 font-normal ml-2">({flags.filter((f) => f.level === 'warn').length} warn · {flags.filter((f) => f.level === 'info').length} info)</span></>}
            open={openSections.flags} onToggle={() => setOpenSections({ ...openSections, flags: !openSections.flags })}
            highlight={flags.some((f) => f.level === 'warn') ? 'amber' : 'gray'}
          >
            <ul className="space-y-2 text-xs">
              {flags.map((f, i) => (
                <li key={i} className={`p-2 border-l-2 ${f.level === 'warn' ? 'border-amber-500 bg-amber-50/50' : 'border-gray-300 bg-gray-50/50'}`}>
                  <div className="flex items-center gap-2">
                    <span className={`uppercase tracking-wider font-bold text-[9px] ${f.level === 'warn' ? 'text-amber-700' : 'text-gray-500'}`}>{f.level} · {f.source}</span>
                  </div>
                  <p className="mt-1 text-gray-700">{f.message}</p>
                  {f.path && <p className="text-[10px] text-gray-400 font-mono mt-1">{f.path}</p>}
                </li>
              ))}
            </ul>
          </Collapsible>
        )}

        {/* Synthesis (Hotspots + Totals) */}
        {synthesis && Object.keys(synthesis).length > 0 && (
          <Collapsible
            title="Synthese · Potenzial-Skizze"
            open={openSections.synthesis} onToggle={() => setOpenSections({ ...openSections, synthesis: !openSections.synthesis })}
          >
            {synthesis.total_annual_cost_eur && (
              <div className="bg-[#0a0a0a] text-white p-4 mb-4">
                <p className="text-[10px] uppercase tracking-wider text-[#befa0a] mb-1">Wirtschaftliches Potenzial</p>
                <p className="text-2xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  ~{parseInt(synthesis.total_annual_cost_eur).toLocaleString('de-DE')} €/Jahr
                </p>
                {synthesis.total_lost_hours_year && (
                  <p className="text-sm text-gray-300 mt-1">
                    ≈ {parseInt(synthesis.total_lost_hours_year).toLocaleString('de-DE')} Stunden/Jahr
                  </p>
                )}
                {synthesis.total_basis && <p className="text-xs text-gray-400 mt-2 italic">Basis: {synthesis.total_basis}</p>}
              </div>
            )}

            {(synthesis.potential_hotspots || []).length > 0 && (
              <>
                <h4 className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>Hotspots</h4>
                <ul className="space-y-2 text-sm mb-4">
                  {synthesis.potential_hotspots.map((h, i) => (
                    <li key={i} className="border border-gray-200 p-3">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className="font-bold">{h.area}</p>
                        <span className="text-[10px] uppercase tracking-wider bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{h.confidence}</span>
                      </div>
                      <p className="text-gray-600 text-xs">{h.description}</p>
                      {(h.magnitude_hours_year || h.magnitude_eur_year) && (
                        <p className="text-xs mt-1 text-[#658a07]">
                          {h.magnitude_hours_year ? `~${parseInt(h.magnitude_hours_year)} Std./Jahr` : ''}
                          {h.magnitude_hours_year && h.magnitude_eur_year ? ' · ' : ''}
                          {h.magnitude_eur_year ? `~${parseInt(h.magnitude_eur_year).toLocaleString('de-DE')} €/Jahr` : ''}
                        </p>
                      )}
                      {h.supporting_domains?.length > 0 && (
                        <p className="text-[10px] text-gray-400 mt-1">Aus: {h.supporting_domains.join(', ')}</p>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {synthesis.honest_no_gos?.length > 0 && (
              <div className="border border-red-200 bg-red-50/30 p-3 mb-3">
                <h4 className="text-xs uppercase tracking-wider text-red-700 font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>Ehrliche No-Gos</h4>
                <ul className="space-y-1 text-sm text-red-900">
                  {synthesis.honest_no_gos.map((n, i) => <li key={i}>· {n}</li>)}
                </ul>
              </div>
            )}

            {synthesis.bridge_to_module2 && (
              <div className="border border-[#658a07]/30 bg-[#befa0a]/10 p-3">
                <h4 className="text-xs uppercase tracking-wider text-[#658a07] font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>Brücke zu Modul 2</h4>
                <p className="text-sm text-[#1f1f1f]">{synthesis.bridge_to_module2}</p>
              </div>
            )}
          </Collapsible>
        )}

        {/* Markdown-Report */}
        {run.report_markdown && (
          <Collapsible
            title={<><FileText size={14} className="inline mr-2" />Gutachten · Markdown</>}
            open={openSections.report} onToggle={() => setOpenSections({ ...openSections, report: !openSections.report })}
          >
            <div className="bg-gray-50 border border-gray-200 p-6 rounded">
              {renderMarkdown(run.report_markdown)}
            </div>
          </Collapsible>
        )}

        {/* Experten-Assessments */}
        {assessments.length > 0 && (
          <Collapsible
            title={`6 Experten-Assessments`}
            open={openSections.assessments} onToggle={() => setOpenSections({ ...openSections, assessments: !openSections.assessments })}
          >
            <div className="space-y-3">
              {assessments.map((a, i) => (
                <div key={i} className="border border-gray-200 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-bold text-sm uppercase tracking-wider" style={{ fontFamily: "'Oswald', sans-serif" }}>{a.domain_key} · {a.expert_role}</p>
                    <span className="text-[10px] uppercase tracking-wider bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{a.confidence_overall}</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-2">{a.summary}</p>
                  {a.findings?.length > 0 && (
                    <ul className="space-y-1 text-xs">
                      {a.findings.map((f, j) => (
                        <li key={j} className="border-l-2 border-gray-200 pl-2">
                          <p className="font-bold text-[#1f1f1f]">{f.headline}</p>
                          <p className="text-gray-600">{f.description}</p>
                          {f.data_evidence && <p className="text-[10px] italic text-gray-400 mt-0.5">Evidenz: {f.data_evidence}</p>}
                          {(f.lost_hours_per_week || f.annual_cost_eur) && (
                            <p className="text-[11px] text-[#658a07] mt-0.5">
                              {f.lost_hours_per_week ? `~${f.lost_hours_per_week} h/Woche` : ''}
                              {f.lost_hours_per_week && f.annual_cost_eur ? ' · ' : ''}
                              {f.annual_cost_eur ? `~${parseInt(f.annual_cost_eur).toLocaleString('de-DE')} €/Jahr` : ''}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {a.compliance_observations?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Compliance</p>
                      <ul className="space-y-1 text-xs">
                        {a.compliance_observations.map((c, j) => (
                          <li key={j}>
                            <span className={`text-[10px] uppercase font-bold mr-2 ${c.severity === 'high' ? 'text-red-600' : c.severity === 'warn' ? 'text-amber-600' : 'text-gray-500'}`}>{c.severity}</span>
                            <strong>{c.area}:</strong> {c.observation}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Collapsible>
        )}

        {/* Intake */}
        {run.intake && (
          <Collapsible
            title="Intake-Daten"
            open={openSections.intake} onToggle={() => setOpenSections({ ...openSections, intake: !openSections.intake })}
          >
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
              <Field label="Firma" value={run.intake.company_name} />
              <Field label="Branche" value={run.intake.industry} />
              <Field label="Standorte" value={run.intake.num_locations} />
              <Field label="Mitarbeiter" value={run.intake.num_employees} />
              <Field label="Vollkosten/Std" value={`${run.intake.avg_hourly_cost_eur} €`} />
              <Field label="Bereiche" value={(run.intake.departments || []).join(', ')} />
              <Field label="Tools" value={(run.intake.current_tools || []).join(', ')} />
              <Field label="Schmerzpunkte" value={(run.intake.pain_points || []).join(' · ')} fullWidth />
              <Field label="Datenlage" value={run.intake.data_situation} fullWidth />
              <Field label="Ziele" value={run.intake.goals} fullWidth />
            </div>
          </Collapsible>
        )}

        {/* Branch Profile */}
        {run.branch_profile && (
          <Collapsible
            title={`Branchenprofil · ${run.branch_profile.display_name} (${run.branch_profile.source})`}
            open={openSections.branch} onToggle={() => setOpenSections({ ...openSections, branch: !openSections.branch })}
          >
            <p className="text-xs text-gray-600 mb-2"><strong>Reibungspunkte:</strong> {(run.branch_profile.typical_friction_points || []).join(' · ')}</p>
            {run.branch_profile.core_kpis?.length > 0 && (
              <ul className="space-y-1 text-xs">
                {run.branch_profile.core_kpis.map((k, i) => (
                  <li key={i}><strong>{k.name}</strong> ({k.unit}): {k.definition}</li>
                ))}
              </ul>
            )}
          </Collapsible>
        )}

        {/* Grounding */}
        {run.grounding && (
          <Collapsible
            title={`Grounding · ${(run.grounding.benchmarks || []).length} Benchmarks`}
            open={openSections.grounding} onToggle={() => setOpenSections({ ...openSections, grounding: !openSections.grounding })}
          >
            {run.grounding.industry_summary && <p className="text-xs text-gray-700 mb-3">{run.grounding.industry_summary}</p>}
            {run.grounding.regulatory_notes && (
              <div className="bg-amber-50 border border-amber-200 p-2 mb-3 text-xs text-amber-900">
                <strong>Regulatorik:</strong> {run.grounding.regulatory_notes}
              </div>
            )}
            {run.grounding.benchmarks?.length > 0 && (
              <ul className="space-y-1 text-xs">
                {run.grounding.benchmarks.map((b, i) => (
                  <li key={i} className="border-l-2 border-blue-200 pl-2">
                    <span className="text-[10px] uppercase mr-1 text-gray-500">[{b.confidence}]</span>
                    <strong>{b.topic}:</strong> {b.statement} <span className="text-[#658a07]">({b.value})</span> <span className="text-gray-400 text-[10px]">— {b.source_name}</span>
                  </li>
                ))}
              </ul>
            )}
          </Collapsible>
        )}
      </div>
    </Modal>
  );
}

// =============================================================================
// Helpers: Modal Shell, Collapsible, Stat, Field
// =============================================================================

function Modal({ children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`bg-white w-full ${wide ? 'max-w-[1100px]' : 'max-w-[680px]'} max-h-[calc(100vh-2rem)] overflow-y-auto`}>
        {children}
      </div>
    </div>
  );
}

function Collapsible({ title, open, onToggle, children, highlight }) {
  const ringClass = highlight === 'amber' ? 'border-amber-300' : 'border-gray-200';
  return (
    <div className={`border ${ringClass}`}>
      <button onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left text-sm font-bold uppercase tracking-wider hover:bg-gray-50"
        style={{ fontFamily: "'Oswald', sans-serif" }}>
        <span className="flex items-center gap-2">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {title}</span>
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="border border-gray-200 p-4">
      <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">{label}</p>
      <p className={`text-2xl font-bold ${color || 'text-[#1f1f1f]'}`} style={{ fontFamily: "'Oswald', sans-serif" }}>{value}</p>
    </div>
  );
}

function Field({ label, value, fullWidth }) {
  return (
    <div className={fullWidth ? 'col-span-2' : ''}>
      <span className="text-[10px] uppercase tracking-wider text-gray-400 mr-2">{label}:</span>
      <span className="text-gray-700">{value || '—'}</span>
    </div>
  );
}
